/**
 * Sprint 15 Ola 7 — AuriaSheet
 * 90vh bottom sheet for aurIA conversation.
 * Reads state from useAuriaContext().
 * Uses non-streaming POST to /api/auria/chat (streaming in Ola 8).
 */

import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuriaContext } from './AuriaContextProvider'
import AuriaIcon from './AuriaIcon'
import AuriaTriageInjury from './AuriaTriageInjury'
import { apiFetch } from '../../lib/apiFetch'
import { AURIA_COMMANDS } from './AuriaCommandRegistry'
import { suggestionsForPath, groupedSuggestionsForPath, followupSuggestions, facilityShortcutFor } from './contextual-suggestions'

export default function AuriaSheet() {
  const {
    isOpen,
    closeSheet,
    pendingContext,
    activeCommand,
    setActiveCommand,
    clearActiveCommand,
    conversationHistory,
    setConversationHistory,
    appendMessage,
    userId,
  } = useAuriaContext()

  const [inputValue, setInputValue] = useState('')
  const [loading, setLoading] = useState(false)
  const [injuryZoneSelected, setInjuryZoneSelected] = useState(false)
  // MACRO MAESTRO ARIA: confirmación antes de ejecutar
  const [pendingAction, setPendingAction] = useState(null)  // { type, params, humanText }
  const [executingAction, setExecutingAction] = useState(false)
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)
  const location = useLocation()

  // Pre-populate input when pendingContext arrives
  useEffect(() => {
    if (pendingContext?.question) {
      setInputValue(pendingContext.question)
    }
  }, [pendingContext])

  // FRENTE C: contexto enriquecido (reasoning + limiting_domain + kb_excerpts)
  const [ariaContext, setAriaContext] = useState(null)
  useEffect(() => {
    if (!isOpen || !userId) return
    apiFetch(`/api/auria/context/${userId}`)
      .then(r => r.ok ? r.json() : null)
      .then(ctx => { if (ctx) setAriaContext(ctx) })
      .catch(() => {})
  }, [isOpen, userId])

  // Load history on open
  useEffect(() => {
    if (!isOpen || !userId) return
    apiFetch(`/api/auria/history?userId=${encodeURIComponent(userId)}&days=7`)
      .then((res) => res.ok ? res.json() : { messages: [] })
      .then(({ messages }) => {
        if (messages && messages.length > 0) {
          setConversationHistory(messages)
        }
      })
      .catch(() => {})
  }, [isOpen, userId, setConversationHistory])

  // Scroll to bottom on new messages
  useEffect(() => {
    if (messagesEndRef.current && typeof messagesEndRef.current.scrollIntoView === 'function') {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [conversationHistory])

  // Focus input when sheet opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300)
    }
  }, [isOpen])

  const handleCommandSelect = useCallback((commandId, chip) => {
    const cmd = AURIA_COMMANDS[commandId]
    if (!cmd) return
    setActiveCommand(commandId)
    if (commandId === 'REPORT_INJURY') {
      setInjuryZoneSelected(false)
      setInputValue('')
    } else {
      setInputValue(chip?.label || cmd.label)
    }
  }, [setActiveCommand])

  const handleBodyZoneSelect = useCallback((zone) => {
    const ZONE_LABELS = {
      shoulder: 'hombro / manguito rotador',
      elbow: 'codo / epicóndilo',
      lumbar: 'lumbar / espalda baja',
      hamstring: 'isquiotibial',
      knee: 'rodilla / rótula',
      quadriceps: 'cuádriceps',
      calf: 'gemelo / sóleo',
      ankle: 'tobillo / LCAE',
      groin: 'aductor / pubis',
    }
    const label = ZONE_LABELS[zone] || zone
    setInjuryZoneSelected(true)
    setInputValue(`Tengo molestia en ${label}. ¿Qué debo hacer?`)
  }, [])

  const handleSend = useCallback(async (textOverride) => {
    const raw = (typeof textOverride === 'string' && textOverride.trim().length > 0)
      ? textOverride
      : inputValue
    const text = (raw || '').trim()
    if (!text || loading) return

    setInputValue('')
    setLoading(true)

    // Add user message + empty aurora placeholder (streaming will fill it)
    const userMsg  = { role: 'user', content: text }
    const ariaSlot = { role: 'auria', content: '', streaming: true }
    setConversationHistory((prev) => [...prev, userMsg, ariaSlot])

    try {
      const token = (await import('../../lib/supabase').then(m => m.supabase.auth.getSession()))
        ?.data?.session?.access_token

      // Cinceladura cables 2026-05-28 · /api/auria/chat NUNCA EXISTIÓ en backend.
      // Endpoint canónico = /api/chat (SSE streaming) en server.js:333 con
      // ariaContext live y prompt de sistema completo. Body shape: messages + userId.
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          messages: [{ role: 'user', content: text }],
          userId,
          // FRENTE C N2: contexto enriquecido para que Aria responda con ciencia real
          reasoning:        ariaContext?.reasoning        || [],
          limiting_domain:  ariaContext?.limiting_domain  || null,
          kb_excerpts:      ariaContext?.kb_excerpts       || [],
          day_plan_summary: ariaContext?.day_plan_summary  || null,
          current_tab:      location?.pathname?.replace('/', '') || null,
        }),
      })

      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: 'Error desconocido' }))
        setConversationHistory((prev) => {
          const updated = [...prev]
          const last = updated[updated.length - 1]
          if (last?.role === 'auria') {
            last.content = `Error: ${err.message || res.status}`
            last.streaming = false
          }
          return updated
        })
        return
      }

      // Read SSE stream
      const reader  = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() // keep incomplete line

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const raw = line.slice(6).trim()
          if (!raw) continue
          // Cinceladura cables-v2 2026-05-28 · backend /api/chat (server.js:333)
          // emite { type: 'token' | 'done', content: string } · NO el formato
          // legacy { delta } + sentinel [DONE] del proxy Anthropic. Antes el
          // parser no avanzaba el texto NI marcaba streaming=false → spinner
          // infinito (síntoma aurIA reportado por founder).
          if (raw === '[DONE]') {
            setConversationHistory((prev) => {
              const updated = [...prev]
              const last = updated[updated.length - 1]
              if (last?.role === 'auria') last.streaming = false
              return updated
            })
            continue
          }
          try {
            const data = JSON.parse(raw)
            // Nuevo formato canónico backend /api/chat
            if (data.type === 'token' && typeof data.content === 'string') {
              setConversationHistory((prev) => {
                const updated = [...prev]
                const last = updated[updated.length - 1]
                if (last?.role === 'auria') last.content += data.content
                return [...updated]
              })
            } else if (data.type === 'done') {
              setConversationHistory((prev) => {
                const updated = [...prev]
                const last = updated[updated.length - 1]
                if (last?.role === 'auria') {
                  // En 'done' el content trae el texto completo · si por algún
                  // motivo no llegaron tokens individuales (race), lo seteamos
                  // como respuesta completa para no dejar el bubble vacío.
                  // COLA:auria-contradice-el-dia · si el servidor corrigió una respuesta que contradecía el día, el `done` manda.
                  if ((data.reemplaza || !last.content) && typeof data.content === 'string') last.content = data.content
                  last.streaming = false
                }
                return updated
              })
            } else if (data.type === 'error') {
              setConversationHistory((prev) => {
                const updated = [...prev]
                const last = updated[updated.length - 1]
                if (last?.role === 'auria') {
                  last.content = `Error: ${data.message || data.content || 'desconocido'}`
                  last.streaming = false
                }
                return updated
              })
            } else if (data.delta !== undefined) {
              // Back-compat por si llega del formato legacy { delta }
              setConversationHistory((prev) => {
                const updated = [...prev]
                const last = updated[updated.length - 1]
                if (last?.role === 'auria') last.content += data.delta
                return [...updated]
              })
            }
          } catch { /* malformed chunk · skip */ }
        }
      }
    } catch (err) {
      setConversationHistory((prev) => {
        const updated = [...prev]
        const last = updated[updated.length - 1]
        if (last?.role === 'auria') {
          last.content = 'Lo siento, hubo un error de conexión. Inténtalo de nuevo.'
          last.streaming = false
        }
        return updated
      })
    } finally {
      setLoading(false)
      clearActiveCommand()
      setInjuryZoneSelected(false)
    }
  }, [inputValue, loading, conversationHistory, setConversationHistory, userId, pendingContext, activeCommand, clearActiveCommand])

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }, [handleSend])

  // ── MACRO MAESTRO ARIA: parsear <aria-action> en respuestas ──────────────────
  // Cuando Aria completa un mensaje, detectar si contiene una acción pendiente
  useEffect(() => {
    if (loading) return
    const lastAria = [...conversationHistory].reverse().find(m => m.role === 'auria' && !m.streaming)
    if (!lastAria?.content) return

    // Parsear <aria-action type="X" params="{}">texto</aria-action>
    const actionMatch = lastAria.content.match(/<aria-action\s+type="([^"]+)"\s*params="([^"]*)"[^>]*>([\s\S]*?)<\/aria-action>/)
    if (actionMatch) {
      const [, type, paramsStr, humanText] = actionMatch
      let params = {}
      try { params = JSON.parse(paramsStr || '{}') } catch (_) {}
      // Solo mostrar confirmación si es una intención ejecutiva nueva.
      // COLA:auria-tarjeta-ejecutada-reaparece: una propuesta nueva sustituye a la anterior aunque sea del mismo tipo
      // (antes la tarjeta se quedaba con el texto y los parametros de la vieja).
      const _texto = humanText.trim()
      const _mismaPropuesta = pendingAction && pendingAction.type === type && pendingAction.humanText === _texto &&
        JSON.stringify(pendingAction.params || {}) === JSON.stringify(params || {})
      if (!_mismaPropuesta) {
        setPendingAction({ type, params, humanText: _texto })
      }
    } else {
      // No hay acción → limpiar
      setPendingAction(null)
    }
  }, [conversationHistory, loading])

  // Ejecutar la intención tipada después de confirmación del usuario
  const executeConfirmedAction = useCallback(async () => {
    if (!pendingAction || !userId) return
    setExecutingAction(true)
    try {
      const token = (await import('../../lib/supabase').then(m => m.supabase.auth.getSession()))
        ?.data?.session?.access_token

      const res = await fetch('/api/auria/execute-intent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          userId,
          intent_type: pendingAction.type,
          params:      pendingAction.params,
          confirmed:   true,
        }),
      })

      const json = res.ok ? await res.json() : { error: 'execute_failed', human_result: 'No pude ejecutar el cambio. Inténtalo de nuevo.' }

      // Añadir el resultado como mensaje de Aria
      appendMessage({
        role: 'auria',
        content: json.human_result || (json.error ? `Error: ${json.error}` : 'Hecho.'),
      })
      setPendingAction(null)
    } catch (err) {
      appendMessage({ role: 'auria', content: 'Error de conexión al ejecutar el cambio.' })
    } finally {
      setExecutingAction(false)
    }
  }, [pendingAction, userId, appendMessage])

  const cancelPendingAction = useCallback(() => {
    setPendingAction(null)
    appendMessage({ role: 'auria', content: 'Entendido, no hago nada. Dime si cambias de opinión.' })
  }, [appendMessage])

  const showInjuryTriage = activeCommand === 'REPORT_INJURY' && !injuryZoneSelected

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            data-testid="auria-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={closeSheet}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.4)',
              zIndex: 200,
            }}
          />

          {/* Sheet */}
          <motion.div
            data-testid="auria-sheet"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              // PIEL F8 · el sheet nace COMPACTO: sin conversación crece con su contenido
              // (header + 3 puertas + input), no un panel 90vh con el medio vacío. Con
              // conversación sube a 90vh para el hilo.
              height: conversationHistory.length > 0 ? '90vh' : 'auto',
              maxHeight: '90vh',
              background: 'var(--bg, #fafaf8)',
              borderRadius: '20px 20px 0 0',
              zIndex: 201,
              display: 'flex',
              flexDirection: 'column',
              maxWidth: 430,
              margin: '0 auto',
            }}
          >
            {/* Drag handle */}
            <div style={{
              width: 36,
              height: 4,
              background: 'var(--border, #e0ddd8)',
              borderRadius: 2,
              margin: '12px auto 0',
            }} />

            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 16px 8px',
              borderBottom: '0.5px solid var(--border, #e0ddd8)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <AuriaIcon variant="default" size={28} />
                <span style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: 'var(--pl-ink)',
                  fontFamily: 'var(--font-sans, system-ui, sans-serif)',
                }}>aurIA</span>
              </div>
              <button
                onClick={closeSheet}
                data-testid="auria-close"
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 16,
                  border: '1px solid var(--border, #e0ddd8)',
                  background: 'transparent',
                  cursor: 'pointer',
                  fontSize: 16,
                  color: 'var(--pl-ink)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                ✕
              </button>
            </div>

            {/* Messages area */}
            <div
              data-testid="auria-messages"
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: '12px 0',
              }}
            >
              {conversationHistory.map((msg, i) => (
                <div
                  key={i}
                  data-testid={`auria-message-${i}`}
                  style={{
                    display: 'flex',
                    justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                    padding: '4px 16px',
                    marginBottom: 4,
                  }}
                >
                  <div style={{
                    maxWidth: '80%',
                    padding: '10px 14px',
                    borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                    background: msg.role === 'user'
                      ? 'var(--prime-terracota, #c0715a)'
                      : 'var(--bg-elevated, #f5f3ef)',
                    color: msg.role === 'user' ? 'white' : 'var(--pl-ink)',
                    fontSize: 14,
                    lineHeight: 1.5,
                    fontFamily: 'var(--font-sans, system-ui, sans-serif)',
                    // COLA:auria-saltos-de-linea · el texto trae párrafos y listas numeradas separados por saltos de línea y la
                    // burbuja los juntaba en un solo bloque («…sesión. 1. Carbohidrato…»). Se respetan los saltos.
                    whiteSpace: 'pre-wrap',
                  }}>
                    {/* Show content (strip aria-action XML · es interno · no para el usuario) */}
                    {(msg.content || '').replace(/<aria-action[\s\S]*?<\/aria-action>/g, '').replace(/<aria-meta[\s\S]*?<\/aria-meta>/g, '').trim()}
                    {msg.streaming && (
                      <span
                        aria-hidden="true"
                        style={{
                          display: 'inline-block',
                          width: 2,
                          height: '1em',
                          background: 'var(--prime-ink-soft, #888)',
                          marginLeft: 2,
                          verticalAlign: 'text-bottom',
                          animation: 'blink 0.9s step-end infinite',
                        }}
                      />
                    )}
                  </div>
                </div>
              ))}

              {loading && (
                <div style={{ padding: '4px 16px', display: 'flex', justifyContent: 'flex-start' }}>
                  <div style={{
                    padding: '10px 14px',
                    borderRadius: '16px 16px 16px 4px',
                    background: 'var(--bg-elevated, #f5f3ef)',
                  }}>
                    <AuriaIcon variant="thinking" size={20} />
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* PIEL F8 · LA SOPA DE 13 MUERE · el AuriaTriage (grid "¿EN QUÉ TE AYUDO?" con 13
               comandos) se retira: las 3 puertas (ContextualChips · abajo) son la única entrada,
               contextuales por pantalla + el campo libre como refuerzo. El sheet nace COMPACTO. */}

            {/* Injury triage */}
            {showInjuryTriage && !loading && (
              <AuriaTriageInjury
                onBodyZoneSelect={handleBodyZoneSelect}
                onBack={() => {
                  clearActiveCommand()
                  setInjuryZoneSelected(false)
                }}
              />
            )}

            {/* P6 · Sugerencias contexto-aware · tap → SE ENVÍA como mensaje
                P7 D3 · Si chip es shortcut a Facilidades · emite window event
                y cierra AuriaSheet (founder cinceladura D3 punto 3) */}
            <ContextualChips
              pathname={location?.pathname}
              hasHistory={conversationHistory.length > 0}
              loading={loading}
              lastAriaMessage={
                [...conversationHistory].reverse().find((m) => m.role === 'auria' && !m.streaming)?.content
              }
              onTap={(text) => {
                const sc = facilityShortcutFor(text)
                if (sc) {
                  try {
                    window.dispatchEvent(new CustomEvent('pl-facility-shortcut', { detail: sc }))
                  } catch (_) {}
                  closeSheet?.()
                  return
                }
                handleSend(text)
              }}
            />

            {/* MACRO MAESTRO ARIA: confirmación antes de ejecutar */}
            {pendingAction && !loading && !executingAction && (
              <div style={{
                margin: '8px 16px',
                padding: '12px 14px',
                background: 'var(--pl-terracota-50, #fdf4f1)',
                border: '1px solid var(--pl-terracota-200, #f0c5b6)',
                borderRadius: 12,
                fontFamily: 'var(--font-sans, system-ui, sans-serif)',
              }}>
                <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--pl-ink)', lineHeight: 1.4 }}>
                  {pendingAction.humanText || '¿Confirmas este cambio en tu plan?'}
                </p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={executeConfirmedAction}
                    style={{
                      flex: 1,
                      padding: '8px 0',
                      borderRadius: 8,
                      border: 'none',
                      background: 'var(--pl-terracota, #c0715a)',
                      color: 'white',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    Confirmar
                  </button>
                  <button
                    onClick={cancelPendingAction}
                    style={{
                      flex: 1,
                      padding: '8px 0',
                      borderRadius: 8,
                      border: '1px solid var(--pl-border, #e0ddd8)',
                      background: 'transparent',
                      fontSize: 14,
                      color: 'var(--prime-ink-soft, #666)',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            {/* Input area */}
            <div style={{
              padding: '8px 12px',
              paddingBottom: 'calc(8px + env(safe-area-inset-bottom, 0px))',
              borderTop: '0.5px solid var(--border, #e0ddd8)',
              display: 'flex',
              gap: 8,
              alignItems: 'flex-end',
              background: 'var(--bg, #fafaf8)',
            }}>
              <textarea
                ref={inputRef}
                data-testid="auria-input"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Escribe a aurIA..."
                rows={1}
                style={{
                  flex: 1,
                  resize: 'none',
                  border: '1px solid var(--border, #e0ddd8)',
                  borderRadius: 20,
                  padding: '10px 14px',
                  // COLA:qa-visual-de-clase · a menos de 16 px iOS amplia la pagina al enfocar: la hoja fija se salia por la
                  // derecha (tarjeta y Cancelar cortados) y los toques no caian donde se veia el control.
                  fontSize: 16,
                  fontFamily: 'var(--font-sans, system-ui, sans-serif)',
                  background: 'var(--bg-elevated, #f5f3ef)',
                  color: 'var(--pl-ink)',
                  outline: 'none',
                  lineHeight: 1.4,
                  maxHeight: 100,
                  overflowY: 'auto',
                }}
              />
              <button
                data-testid="auria-send"
                onClick={handleSend}
                disabled={!inputValue.trim() || loading}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 20,
                  border: 'none',
                  background: 'var(--prime-terracota, #c0715a)',
                  cursor: !inputValue.trim() || loading ? 'not-allowed' : 'pointer',
                  opacity: !inputValue.trim() || loading ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {loading
                  ? <AuriaIcon variant="listening" size={18} />
                  : <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M22 2L11 13" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M22 2L15 22 11 13 2 9l20-7z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                }
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

// P6 · ContextualChips · sugerencias estables contexto-aware (founder C.1)
// Tap → handleSend(text) → SE ENVÍA como mensaje · NO se queda en input.
// Si ya hay conversación, rotan a followup para abrir conversación nueva.
const _CHIP_STYLE = {
  background: 'var(--bg-elevated, #f5f3ef)', border: '1px solid var(--border, #e0ddd8)',
  borderRadius: 16, padding: '6px 12px', fontSize: 12, fontWeight: 500, color: 'var(--pl-ink)',
  cursor: 'pointer', fontFamily: 'var(--font-sans, system-ui, sans-serif)', minHeight: 32, transition: 'background 0.15s',
}
function _Chip({ text, onTap }) {
  return (
    <button type="button" onClick={() => onTap?.(text)}
      aria-label={`Sugerencia: ${text} · tap envía como mensaje`} style={_CHIP_STYLE}>{text}</button>
  )
}
// FRENTE F · 3 bloques (entender/ajustar/avisar) · contexto por pantalla · campo libre debajo (ya existe).
const _BLOCK_LABEL = { entender: 'Entender', ajustar: 'Ajustar', avisar: 'Avisar' }
function ContextualChips({ pathname, hasHistory, loading, lastAriaMessage, onTap }) {
  if (loading) return null
  const wrap = { padding: '10px 12px 4px', borderTop: '0.5px solid var(--border, #e0ddd8)', background: 'var(--bg, #fafaf8)' }
  // Seguimiento de conversación → fila plana (hilo del último mensaje)
  if (hasHistory) {
    const chips = followupSuggestions(lastAriaMessage)
    if (!chips || chips.length === 0) return null
    return (
      <div role="group" aria-label="Sugerencias aurIA" style={{ ...wrap, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {chips.map((c, i) => <_Chip key={i} text={c} onTap={onTap} />)}
      </div>
    )
  }
  // Estado inicial → 3 puertas según la pantalla
  const grouped = groupedSuggestionsForPath(pathname)
  if (!grouped) return null
  return (
    <div role="group" aria-label="Sugerencias aurIA · entender, ajustar, avisar" style={wrap}>
      {['entender', 'ajustar', 'avisar'].map((block) => {
        const items = grouped[block] || []
        if (items.length === 0) return null
        return (
          <div key={block} style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--pl-muted, #888)', marginBottom: 5 }}>
              {_BLOCK_LABEL[block]}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {items.map((c, i) => <_Chip key={i} text={c} onTap={onTap} />)}
            </div>
          </div>
        )
      })}
    </div>
  )
}
